import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, GitBranch, GitCommitHorizontal, History, PartyPopper, RotateCw, ShieldCheck, Sparkles, Square, Wand2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Page, PageHeader, useProjectSlug, useWorkspaceSlug } from "@/app";
import {
  ActorChip,
  AILabel,
  AbsoluteTime,
  askGuv,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  CodeBlock,
  CopyButton,
  DataList,
  ErrorState,
  ExplainPanel,
  Loading,
  RelativeTime,
  ShowAsCode,
  Skeleton,
  StatusBadge,
  toast,
} from "@/design";
import {
  api,
  ApiError,
  idem,
  qk,
  unwrap,
  useBuild,
  useCancelDeployment,
  useDeploymentExplanation,
  useDeploymentLogs,
  useDeployments,
  useOperation,
  useOperationStream,
  type Build,
  type DeployExplanation,
  type Deployment,
  type FixAction,
  type Operation,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatBytes, formatDuration, shortSha } from "@/lib/format";
import { BuildLog, phasesFromLines, phasesFromOperation } from "./-components/BuildLog";
import { DeployActionDialog } from "./-components/DeployActions";
import { ChangeSummary, DeployStatusIcon } from "./-components/DeploymentRow";
import { compareUrl, failureLabel, isInFlight, previousOf, triggerLabel } from "./-components/deploy-utils";

export const Route = createFileRoute("/$workspace/$project/deployments/$deploymentId")({
  staticData: { title: "Deployment" },
  component: DeploymentScreen,
});

function useLiveDeployment(id: string) {
  return useQuery({
    queryKey: qk.deployment(id),
    queryFn: () => unwrap(api.GET("/v1/deployments/{deployment_id}", { params: { path: { deployment_id: id } } })),
    refetchInterval: (q) => (q.state.data && isInFlight(q.state.data.status) ? 2000 : false),
  });
}

function DeploymentScreen() {
  const { deploymentId } = Route.useParams();
  const project = useProjectSlug();
  const workspace = useWorkspaceSlug();
  const dep = useLiveDeployment(deploymentId);

  if (dep.isPending) {
    return (
      <Page>
        <Loading label="Loading deploy">
          <Skeleton className="mb-3 h-4 w-48" />
          <Skeleton className="mb-8 h-9 w-2/3" />
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <Skeleton className="h-96 rounded-xl" />
            <Skeleton className="h-72 rounded-xl" />
          </div>
        </Loading>
      </Page>
    );
  }
  if (dep.isError) {
    const notFound = dep.error instanceof ApiError && dep.error.status === 404;
    return (
      <Page>
        <ErrorState
          variant="page"
          error={dep.error}
          title={notFound ? "We could not find that deploy" : undefined}
          onRetry={notFound ? undefined : () => dep.refetch()}
        />
        <div className="text-center">
          <Button asChild variant="link">
            <Link to="/$workspace/$project/deployments" params={{ workspace, project }} search={((s: Record<string, unknown>) => s) as never}>
              Back to all deployments
            </Link>
          </Button>
        </div>
      </Page>
    );
  }
  return <DeploymentDetail d={dep.data} />;
}

function DeploymentDetail({ d }: { d: Deployment }) {
  const project = useProjectSlug();
  const workspace = useWorkspaceSlug();
  const qc = useQueryClient();
  const live = isInFlight(d.status);
  const failed = d.status === "failed" || d.status === "crashed";
  const [dialog, setDialog] = useState<null | "rollback" | "redeploy">(null);
  const explanation = useDeploymentExplanation(d.id, { enabled: failed });
  const logs = useDeploymentLogs(d.id);
  const build = useBuild(d.build_id);
  const op = useOperation(live ? d.operation_id : null);
  const stream = useOperationStream(live ? d.operation_id : null);
  const history = useDeployments(project, { service: d.service_name, environment: d.environment_name, limit: 50 });
  const previous = history.data ? previousOf(history.data.data, d) : undefined;
  const cancel = useCancelDeployment(project);
  const [justFinished, setJustFinished] = useState(false);

  // When the live stream ends, refresh the record and its log.
  useEffect(() => {
    if (stream.finished) {
      setJustFinished(true);
      void qc.invalidateQueries({ queryKey: qk.deployment(d.id) });
      void qc.invalidateQueries({ queryKey: qk.deploymentLogs(d.id) });
      void qc.invalidateQueries({ queryKey: qk.project(project) });
    }
  }, [stream.finished, d.id, project, qc]);

  const logLines = logs.data && typeof logs.data === "object" ? logs.data.lines : [];
  const phases = useMemo(() => {
    if (live || (justFinished && !logLines.length)) return phasesFromOperation(op.data, stream.events);
    return phasesFromLines(logLines, { failed, finishedAt: d.finished_at });
  }, [live, justFinished, op.data, stream.events, logLines, failed, d.finished_at]);

  const backSearch = ((s: Record<string, unknown>) => ({ env: s.env })) as never;
  const commit = d.source?.commit;

  return (
    <Page>
      <Link
        to="/$workspace/$project/deployments"
        params={{ workspace, project }}
        search={backSearch}
        className="mb-4 inline-flex items-center gap-1.5 rounded text-sm font-medium text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" /> All deployments
      </Link>
      <PageHeader
        eyebrow={
          <span className="inline-flex flex-wrap items-center gap-1.5">
            Deploy of <span className="font-semibold text-text">{d.service_name}</span> to
            <Badge size="sm" variant="outline" tone={d.environment_name === "production" ? "accent" : "warn"}>
              {d.environment_name}
            </Badge>
          </span>
        }
        title={commit?.message ?? triggerLabel(d)}
        meta={
          <>
            <StatusBadge kind="deployment" status={d.status} variant="pill" />
            {commit?.sha && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 font-mono text-xs text-text">
                <GitCommitHorizontal className="size-3.5 text-subtle" aria-hidden="true" />
                {commit.url ? (
                  <a href={commit.url} target="_blank" rel="noreferrer" className="hover:text-accent-text hover:underline" aria-label={`Commit ${shortSha(commit.sha)} on GitHub`}>
                    {shortSha(commit.sha)}
                  </a>
                ) : (
                  shortSha(commit.sha)
                )}
              </span>
            )}
            {commit?.branch && (
              <span className="inline-flex items-center gap-1 text-sm text-muted">
                <GitBranch className="size-3.5" aria-hidden="true" /> {commit.branch}
              </span>
            )}
            <span className="text-sm text-muted">{triggerLabel(d)} by</span>
            <ActorChip actor={d.trigger.actor} size="sm" />
            <span className="text-sm text-muted">
              · <RelativeTime value={d.created_at} />
            </span>
          </>
        }
        actions={
          <>
            {live && d.status !== "pre_deploy" && (
              <Button
                icon={<Square />}
                loading={cancel.isPending}
                onClick={() =>
                  cancel.mutate(d.id, {
                    onSuccess: () => {
                      toast.info("Deploy cancelled", { description: "The version that was already live keeps serving." });
                      void qc.invalidateQueries({ queryKey: qk.deployment(d.id) });
                    },
                    onError: (e) => toast.error(e),
                  })
                }
              >
                Cancel deploy
              </Button>
            )}
            {(d.status === "active" || d.status === "crashed") && (
              <Button icon={<RotateCw />} onClick={() => setDialog("redeploy")}>
                Redeploy
              </Button>
            )}
            {d.status === "superseded" && (
              <Button variant="primary" icon={<History />} onClick={() => setDialog("rollback")}>
                Roll back to this
              </Button>
            )}
          </>
        }
      />

      {failed && <FailureBanner d={d} explanation={explanation.data} loading={explanation.isPending} />}
      {d.status === "waiting_approval" && (
        <Callout tone="warn" title="This deploy is waiting for approval" className="mb-6">
          Production is protected, so an owner or admin has to approve it. They have been notified on their phone.
        </Callout>
      )}
      {justFinished && d.status === "active" && (
        <Callout
          tone="ok"
          icon={<PartyPopper />}
          title={`${d.service_name} is live`}
          className="mb-6"
          actions={
            d.url ? (
              <Button asChild size="sm" variant="secondary" iconRight={<ArrowUpRight />}>
                <a href={d.url} target="_blank" rel="noreferrer">
                  Open the app
                </a>
              </Button>
            ) : undefined
          }
        >
          The new version passed its health check and is now serving everyone. The old one is draining.
        </Callout>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          {logs.isError && !live ? (
            <ErrorState error={logs.error} onRetry={() => logs.refetch()} title="We could not load the log" />
          ) : logs.isPending && !live ? (
            <Skeleton className="h-80 rounded-xl" />
          ) : (
            <BuildLog
              phases={phases}
              live={live}
              emptyText={live ? "Waiting for a build slot. Builds are free and usually start within seconds." : d.status === "cancelled" ? "Cancelled before anything ran." : "No log lines for this deploy."}
            />
          )}
          <Card>
            <CardHeader title="What changed" description={previous ? <>Compared with the deploy from <RelativeTime value={previous.created_at} /></> : "Compared with the previous deploy"} />
            <CardBody>
              <ChangeSummary d={d} previous={previous} compare={compareUrl(previous, d)} />
            </CardBody>
          </Card>
        </div>
        <aside className="space-y-6" aria-label="Deploy details">
          <DetailsCard d={d} build={build.data} />
          <ScanCard build={build.data} loading={!!d.build_id && build.isPending} />
        </aside>
      </div>
      {dialog && <DeployActionDialog kind={dialog} deployment={d} open onOpenChange={(o) => !o && setDialog(null)} />}
    </Page>
  );
}

function DetailsCard({ d, build }: { d: Deployment; build?: Build }) {
  // Time to live: from the deploy being created to it serving traffic.
  const timeToLive = d.duration_ms ?? (d.active_at ? new Date(d.active_at).getTime() - new Date(d.created_at).getTime() : null);
  const detected = build?.detected;
  const builtWith =
    [detected?.framework, detected?.language && `${detected.language} ${detected.language_version ?? ""}`.trim()].filter(Boolean).join(" · ") ||
    (build?.builder === "dockerfile" ? "Your Dockerfile" : build?.builder === "railpack" ? "Railpack" : build?.builder);
  const items = [
    { term: "Status", value: <StatusBadge kind="deployment" status={d.status} /> },
    { term: "Started", value: <AbsoluteTime value={d.started_at ?? d.created_at} className="text-sm" /> },
    { term: "Time to live", value: timeToLive != null ? formatDuration(timeToLive) : isInFlight(d.status) ? "In progress" : "–" },
    { term: "Built with", value: builtWith || "–" },
    { term: "Build cache", value: build?.cache_hit_ratio != null ? `${Math.round(build.cache_hit_ratio * 100)}% reused` : "–" },
    { term: "Image size", value: build?.image?.size_bytes ? formatBytes(build.image.size_bytes) : "–" },
    {
      term: "Image",
      value: d.image?.digest ? (
        <span className="inline-flex items-center gap-1">
          <code className="font-mono text-xs">{d.image.digest.slice(7, 19)}</code>
          {d.image.signed && (
            <Badge size="sm" tone="ok" icon={<ShieldCheck />}>
              Signed
            </Badge>
          )}
        </span>
      ) : (
        "–"
      ),
    },
    { term: "Deploy ID", value: <span className="inline-flex items-center gap-0.5"><code className="truncate font-mono text-xs">{d.id}</code><CopyButton value={d.id} label="Copy deploy ID" className="size-6" /></span> },
  ];
  return (
    <Card>
      <CardHeader title="Details" />
      <CardBody className="pt-0">
        <DataList items={items} />
        {d.url && (d.status === "active" || d.status === "superseded") && (
          <a href={d.url} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border bg-surface-2/60 px-3 py-2 text-sm font-medium text-accent-text hover:border-border-strong">
            <span className="truncate">{d.url.replace(/^https:\/\//, "")}</span>
            <ArrowUpRight className="size-4 shrink-0" aria-hidden="true" />
          </a>
        )}
      </CardBody>
    </Card>
  );
}

function ScanCard({ build, loading }: { build?: Build; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 rounded-xl" />;
  const scan = build?.scan;
  if (!scan) return null;
  const v = scan.vulnerabilities ?? {};
  const rows = [
    { label: "Secrets in your code", n: scan.secrets_found ?? 0, bad: (scan.secrets_found ?? 0) > 0 },
    { label: "Secrets sent to browsers", n: scan.secrets_in_bundle ?? 0, bad: (scan.secrets_in_bundle ?? 0) > 0 },
    { label: "Critical vulnerabilities", n: v.critical ?? 0, bad: (v.critical ?? 0) > 0 },
    { label: "High vulnerabilities", n: v.high ?? 0, bad: (v.high ?? 0) > 0 },
    { label: "Known to be exploited", n: v.kev ?? 0, bad: (v.kev ?? 0) > 0 },
  ];
  return (
    <Card>
      <CardHeader
        title="Safety checks"
        description={scan.blocked ? "This build was blocked before it could go live." : "Run on every build, before anything goes live."}
        actions={
          <ExplainPanel title="What we check on every build" side="left">
            <p>We scan your code and the files we send to browsers for secrets, and your dependencies for known vulnerabilities.</p>
            <p>A secret in the browser bundle <strong>blocks the deploy</strong>, because anyone could read it.</p>
          </ExplainPanel>
        }
      />
      <CardBody className="pt-0">
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.label} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="text-muted">{r.label}</span>
              <span className={cn("tabular font-medium", r.bad ? "text-danger-text" : "text-ok-text")}>{r.n === 0 ? "None" : r.n}</span>
            </li>
          ))}
        </ul>
        {(v.medium || v.low) ? <p className="mt-2 text-xs text-subtle">Also {v.medium ?? 0} medium and {v.low ?? 0} low. See the Safety Centre for details.</p> : null}
      </CardBody>
    </Card>
  );
}

/** The failure screen (docs/12 §4): one-sentence cause, the fix, [Apply], "Ask AI to fix". */
function FailureBanner({ d, explanation: x, loading }: { d: Deployment; explanation?: DeployExplanation; loading: boolean }) {
  const project = useProjectSlug();
  const workspace = useWorkspaceSlug();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const actions = x?.fix?.actions ?? [];
  const oneClick = actions.find((a) => a.kind === "one_click" || a.kind === "api");
  const command = actions.find((a) => a.kind === "command");
  const pr = actions.find((a) => a.kind === "open_pr");
  const docs = actions.find((a) => a.kind === "docs");

  const apply = useMutation({
    mutationFn: async (a: FixAction) => {
      if (a.api) {
        await unwrap(api.request(a.api.method.toLowerCase() as never, a.api.path as never, { body: a.api.body, headers: idem() } as never) as never);
      }
      return unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/services/{service}/deployments", {
          params: { path: { project, environment: d.environment_name ?? "production", service: d.service_name ?? d.service_id }, header: idem() },
          body: { message: `Fix: ${a.label}`, clear_build_cache: false },
        }),
      ) as Promise<Operation>;
    },
    onSuccess: (op) => {
      void qc.invalidateQueries({ queryKey: qk.project(project) });
      toast.success("Fix applied. Redeploying now.", { description: "We'll switch traffic once the new version is healthy." });
      if (op.resource?.type === "deployment") {
        void navigate({ to: "/$workspace/$project/deployments/$deploymentId", params: { workspace, project, deploymentId: op.resource.id }, search: ((s: Record<string, unknown>) => s) as never });
      }
    },
    onError: (e) => toast.error(e),
  });

  if (loading) return <Skeleton className="mb-6 h-24 rounded-lg" />;
  const cause = x?.status === "available" ? x.cause : d.failure?.message ?? d.status_text ?? "This deploy did not go live.";
  const title = d.failure?.category ? failureLabel[d.failure.category] : "This deploy did not go live";

  return (
    <div className="mb-6 space-y-3">
      <Callout
        tone="danger"
        title={cause}
        actions={
          <>
            {oneClick && (
              <Button variant="primary" size="sm" icon={<Wand2 />} loading={apply.isPending} onClick={() => apply.mutate(oneClick)}>
                Apply
              </Button>
            )}
            {pr && (
              <Button
                size="sm"
                icon={<Sparkles />}
                onClick={() =>
                  askGuv({
                    question: `Open a pull request that fixes this failed deploy of ${d.service_name}: ${cause}`,
                    context: { deployment_id: d.id, category: d.failure?.category, action: "open_pr", suggestion: pr.description },
                  })
                }
              >
                Ask AI to fix
              </Button>
            )}
          </>
        }
      >
        <span className="block">
          <span className="sr-only">{title}. </span>
          {x?.fix?.summary ? (
            <>
              <strong className="font-semibold">Fix:</strong> {x.fix.summary}
            </>
          ) : x?.status === "pending" ? (
            "We are working out what went wrong. This usually takes a few seconds."
          ) : (
            "Nothing that was live changed, so nobody saw an outage."
          )}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
          {oneClick && <span>Apply will: {oneClick.label.toLowerCase()}.</span>}
          {x?.ai_generated ? <AILabel /> : x?.generated_by === "rules" ? <span>Diagnosed from your logs{x.confidence ? ` (${x.confidence} confidence)` : ""}.</span> : null}
        </span>
      </Callout>

      {x?.status === "available" && (
        <details className="group rounded-lg border border-border bg-surface px-4 py-3 open:pb-4">
          <summary className="cursor-pointer select-none list-none text-sm font-medium text-text marker:hidden">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-subtle transition-transform group-open:rotate-90" aria-hidden="true">›</span>
              What we saw, and other ways to fix it
            </span>
          </summary>
          <div className="mt-3 grid gap-4 lg:grid-cols-2">
            {x.evidence && x.evidence.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-subtle">Evidence</div>
                <ul className="space-y-1.5">
                  {x.evidence.map((e, i) => (
                    <li key={i} className="rounded-md border border-border bg-surface-2 px-3 py-2">
                      <div className="mb-0.5 flex items-center justify-between gap-2 text-2xs uppercase tracking-wide text-subtle">
                        <span>{e.source?.replace(/_/g, " ")}</span>
                        {e.timestamp && <RelativeTime value={e.timestamp} />}
                      </div>
                      <code className="block whitespace-pre-wrap break-all font-mono text-xs text-text">{e.text}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-3">
              {command?.command && <CodeBlock title={command.label} code={command.command} language="shell" />}
              {oneClick?.manifest_patch && <CodeBlock title="Or change guv.yaml" code={oneClick.manifest_patch} language="diff" />}
              <div className="flex flex-wrap items-center gap-2">
                {oneClick && (
                  <ShowAsCode
                    title={`Fix: ${oneClick.label}`}
                    cli={oneClick.command ?? command?.command ?? `guv deploy --service ${d.service_name} --env ${d.environment_name}`}
                    manifestDiff={oneClick.manifest_patch}
                    apiCall={oneClick.api ? { method: oneClick.api.method, path: oneClick.api.path, body: oneClick.api.body } : undefined}
                  />
                )}
                {docs?.url && (
                  <a href={docs.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline">
                    {docs.label} <ArrowUpRight className="size-3.5" aria-hidden="true" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

export { DeployStatusIcon };
